/**
 * `AlertHost`: draws v2's alert (`@rista/core/alert`), mounted ONCE by each app's shell. One body for
 * both apps (the user, 2026-10-05: "v2 modal (revamp the modal too)"), in web v1's warning modal's
 * layout (`homeLayout`'s info modal): a "Warning" heading, the message, and an OK button at the end,
 * padded 32; type and colours are roles (`look.ts`, QD8). Closed by OK, the backdrop, Escape on web and back on Android.
 *
 * On the library's `Sheet` since L0 (the user, 2026-10-06): a `narrow` centred dialog on web (v1's
 * 500-wide card, radius 10, MUI's shadow), and on a phone a bottom sheet in its `modal`
 * presentation, so it shows above any v1 modal that is open (a print skip notice can be raised from
 * inside one).
 */
import React, { useRef, useSyncExternalStore } from "react";
import { Text, View } from "react-native";

import { alerts } from "@rista/core/alert";
import { copy } from "@rista/core/copy";
import { Button } from "@rista/ui/library/components/Button";
import { Sheet } from "@rista/ui/library/components/Sheet";
import { useTheme } from "@rista/ui/library/theme/ThemeProvider";
import { lookFor } from "@rista/ui/library/theme/variants";
import { alertLook } from "./look";

export function AlertHost() {
  const alert = useSyncExternalStore(alerts.subscribe, alerts.current, alerts.current);
  // the alert showing, or after a dismiss the last one, which the sheet draws while it fades out
  const shownRef = useRef(alert);
  if (alert) shownRef.current = alert;
  const drawn = shownRef.current;
  const look = lookFor(alertLook, useTheme());
  return (
    <Sheet
      visible={!!alert}
      onClose={alerts.dismiss}
      closeLabel={copy("alert.close")}
      testID="alert"
      role="alertdialog"
      size="narrow"
      presentation="modal"
      appearance="pos"
    >
      {!!drawn && (
        <View style={look.body}>
          <Text role="heading" style={look.title}>
            {drawn.title ?? copy("alert.warning")}
          </Text>
          <Text style={look.message}>{drawn.message}</Text>
          <View style={look.actions}>
            <Button testID="alert-ok" label={copy("alert.ok")} variant="primary" size="sm" onPress={alerts.dismiss} />
          </View>
        </View>
      )}
    </Sheet>
  );
}
